import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import get from 'lodash/get';
import head from 'lodash/head';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { getFilteredProducts } from '@yme/api';
import { usePaginationConfig } from '@yme/atoms/pagination';
import { QueryParams, VARIANTS_ACCESSORY_TYPE, VARIANTS_ACCESSORY_TYPE_EN, VARIANTS_PRICES_DISCOUNT, VARIANT_SKU_DESC, } from '@yme/common/constants';
import { useWindowSize, useIsMount } from '@yme/hooks/index';
import { getBloomreachModel, getIsInAuthor, getNavigationBreadCrumbs } from '@yme/selectors/common';
import { useTranslation } from '@yme/services/i18n';
import { PixelPageView, setPixelPageView } from '@yme/utils/bloomreach';
import { DEFAULT_START_PAGE, INITIAL_PAGINATION_PARAM } from '../common/constants';
import { buildCounterParameters, buildPaginationData, buildRequestParam, getDefaultPerPageLimit, getFacetsValues, getFacetsData, getParamsFromURL, getSelectedFacetsFromUrl, mergeProductFilterFacetsCounts, } from '../common/helpers';
import { useSortFilterOptions } from '../common/useSortFilterOptions';
import { getPillsOptions, getPreparedData } from './helpers';
export function useProductFilter(props) {
    const { categoryIds, productEndpoint = '', filterFacetsConfig, compatiblePanelAPI, sortOptionsType, perPageLimitSetting, isDiscountMode, isCMPPage, productType, } = props;
    const isCompatibleProductChecked = get(compatiblePanelAPI, 'compatibleProduct.isCompatibleChecked', false);
    const compatibleProductKey = get(compatiblePanelAPI, 'compatibleProduct.key', null);
    const isInAuthor = useSelector(getIsInAuthor);
    const navigationBreadCrumbs = useSelector(getNavigationBreadCrumbs);
    const { productCatalog } = useSelector(getBloomreachModel);
    const history = useHistory();
    const queryParams = new URLSearchParams(history.location.search);
    const isDiscountFromAEM = isDiscountMode || (isCMPPage && queryParams.get(QueryParams.DISCOUNTED_MODE));
    const isMount = useIsMount();
    const { isMobileView } = useWindowSize();
    const { t, dictionary } = useTranslation();
    const initialResult = useRef([]);
    const [resultsData, setResultsData] = useState(initialResult.current);
    const [isDataExists, setIsDataExists] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [facets, setFacets] = useState([]);
    const [isFacetsLoaded, setIsFacetsLoaded] = useState(false);
    const [isShowOnlyDiscounted, setIsShowOnlyDiscounted] = useState(false);
    const facetsConfig = useMemo(() => filterFacetsConfig.map((id) => id), [filterFacetsConfig]);
    const { 
    // params from url
    pageParam, perPageParam, searchParam, sortOrderParam, facetsParams, discountedParam, } = useMemo(() => {
        const url = history.location.search;
        const res = getParamsFromURL(url, facetsConfig);
        return res;
    }, [facetsConfig, history.location.search]);
    // additional options
    const [isFilterCleared, setIsFilterCleared] = useState(true);
    // for searching
    const [search, setSearch] = useState(searchParam ?? '');
    // sorting functionality
    const { sortFilterOptions } = useSortFilterOptions({
        type: sortOptionsType,
    });
    const defaultSortOption = sortFilterOptions.find((option) => Boolean(option?.isValueDefault)) || sortFilterOptions[0];
    const sortOrderValue = sortOrderParam ?? defaultSortOption.value;
    const [sortOrder, setSortOrder] = useState(sortOrderValue);
    // For pagination
    const currentPageValue = Number(pageParam) || DEFAULT_START_PAGE;
    const perPageLimit = Number(perPageParam) || getDefaultPerPageLimit(window.innerWidth, perPageLimitSetting);
    const [paginationParam, setPaginationParam] = useState({
        ...INITIAL_PAGINATION_PARAM,
        currentPage: currentPageValue,
        activeElement: perPageLimit,
    });
    const [currentPage, setCurrentPage] = useState(currentPageValue);
    const [amountPerPage, setAmountPerPage] = useState(perPageLimit);
    // Counter state
    const [counter, setCounter] = useState({
        from: currentPage,
        till: currentPage * amountPerPage,
        total: 0,
    });
    const { paginationConfig, activeItemValue: paginationActivePage, onClickItemHandler: onClickActivePageHandler, currentPerPageValue, setCurrentPerPageValue, setActiveItemValue, } = usePaginationConfig({
        paginationData: paginationParam,
        isMobileView,
    });
    const selectedFacets = useMemo(() => {
        if (facets?.length) {
            const finallyFacetsResult = facets
                .filter((facet) => facet.values.some((value) => Boolean(value.checked)))
                .map((facet) => {
                const result = `${facet.id}: ${facet.values
                    .filter((value) => value.checked)
                    .map((value) => `"${value.value}"`)
                    .join(',')}`;
                return result;
            })
                .join('|');
            return finallyFacetsResult;
        }
        const facetsFromUrl = getSelectedFacetsFromUrl(facetsParams);
        return facetsFromUrl;
    }, [facets, facetsParams]);
    const parameters = useMemo(() => {
        const { extraQueryParam } = getPreparedData({
            isShowOnlyDiscounted,
            isCompatibleProductChecked,
            compatibleProductKey,
            isInAuthor,
        });
        const param = buildRequestParam({
            categoryIds,
            facetsConfig,
            selectedFacets,
            sortOrder,
            amountPerPage,
            currentPage,
            search,
            extraQueryParam,
        });
        return param
            ? {
                ...param,
                sortOrder: [param.sortOrder, VARIANT_SKU_DESC].join('|'),
            }
            : param;
    }, [
        isShowOnlyDiscounted,
        isCompatibleProductChecked,
        compatibleProductKey,
        isInAuthor,
        categoryIds,
        facetsConfig,
        selectedFacets,
        sortOrder,
        amountPerPage,
        currentPage,
        search,
    ]);
    // Initial request for getting data from hyperdrive (for storing pristine filters)
    useEffect(() => {
        if (categoryIds?.length && parameters) {
            (async () => {
                try {
                    setIsLoading(true);
                    const response = await getFilteredProducts({
                        endpoint: productEndpoint,
                        config: {
                            ...parameters,
                            productType,
                            selectedFacets: '',
                            search: '',
                        },
                    });
                    const responseFacets = response && response?.facets;
                    if (responseFacets) {
                        const preparedFacetsData = getFacetsData({
                            facetsData: response.facets,
                            facetsConfig,
                            dictionary,
                        });
                        setFacets(getFacetsValues(preparedFacetsData, facetsParams));
                        setIsFacetsLoaded(true);
                    }
                    setIsLoading(false);
                }
                catch (err) {
                    setIsLoading(false);
                    setError(err);
                }
            })();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // Request for get new data (update data) for filter and show results (by selected options)
    useEffect(() => {
        if (!isFacetsLoaded || !categoryIds.length || !parameters) {
            return;
        }
        (async () => {
            try {
                setIsLoading(true);
                const response = await getFilteredProducts({
                    endpoint: productEndpoint,
                    config: {
                        ...parameters,
                        productType,
                    },
                });
                const resultData = response?.results;
                if (response) {
                    setResultsData(resultData);
                    setIsDataExists(true);
                    setIsFilterCleared(false);
                    setFacets((prevFacets) => mergeProductFilterFacetsCounts(prevFacets, response.facets));
                    setCounter((prev) => ({
                        ...prev,
                        total: response.total,
                    }));
                }
                setIsLoading(false);
            }
            catch (err) {
                setError(err);
                setIsLoading(false);
            }
        })();
    }, [categoryIds.length, parameters, productEndpoint, isFacetsLoaded, productType]);
    useEffect(() => {
        if (productCatalog && categoryIds.length && parameters && isFacetsLoaded && !isInAuthor) {
            setPixelPageView(PixelPageView.CATEGORY, {
                cat_id: get(head(categoryIds), 'id', ''),
                cat: navigationBreadCrumbs.join('|'),
                domain_key: productCatalog,
            });
        }
    }, [categoryIds, parameters, navigationBreadCrumbs, isFacetsLoaded, productCatalog, isInAuthor]);
    // Pagination + calculating counter hook
    useEffect(() => {
        if (paginationActivePage !== currentPage || counter.till !== amountPerPage) {
            setCurrentPage(paginationActivePage);
            const { fromValue, tillValue } = buildCounterParameters(paginationActivePage, amountPerPage);
            setCounter({
                ...counter,
                from: fromValue,
                till: tillValue,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationActivePage, amountPerPage]);
    // Pagination hook
    useEffect(() => {
        if (isLoading) {
            return;
        }
        if (resultsData === initialResult.current) {
            return;
        }
        const lengthData = resultsData.length;
        const totalItems = counter.total;
        if (lengthData > 0 && totalItems > 0) {
            const paginationData = buildPaginationData(totalItems, amountPerPage);
            setPaginationParam({
                ...paginationData,
                currentPage: currentPageValue,
                activeElement: amountPerPage,
            });
        }
        if (lengthData === 0) {
            const paginationData = buildPaginationData(1, amountPerPage);
            setPaginationParam({
                ...paginationData,
                currentPage: 1,
                activeElement: amountPerPage,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resultsData, counter, amountPerPage, isLoading, initialResult.current]);
    // Pagination hook
    useEffect(() => {
        if (currentPerPageValue !== amountPerPage) {
            setAmountPerPage(currentPerPageValue);
            setActiveItemValue(DEFAULT_START_PAGE);
            setCurrentPage(DEFAULT_START_PAGE);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPerPageValue]);
    // Pagination + location functionality hook
    useEffect(() => {
        if (isMount) {
            return;
        }
        if (Number(pageParam) !== paginationActivePage ||
            Number(perPageParam) !== currentPerPageValue) {
            const queryParams = new URLSearchParams(history.location.search);
            queryParams.set(QueryParams.PAGE, String(paginationActivePage));
            queryParams.set(QueryParams.PER_PAGE, String(currentPerPageValue));
            history.push({
                search: queryParams.toString(),
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationActivePage, currentPerPageValue]);
    // general for filter hook
    useEffect(() => {
        setFacets((prev) => getFacetsValues(prev, facetsParams));
        setIsShowOnlyDiscounted(Boolean(discountedParam) || Boolean(isDiscountFromAEM));
        setSearch(searchParam ?? '');
        setPaginationParam((prev) => ({
            ...prev,
            currentPage: currentPageValue,
            activeElement: perPageLimit,
        }));
        setCurrentPage(currentPageValue);
        setAmountPerPage(perPageLimit);
        setSortOrder(sortOrderValue);
    }, [
        currentPageValue,
        facetsParams,
        perPageLimit,
        searchParam,
        setSortOrder,
        sortOrderValue,
        discountedParam,
        isDiscountFromAEM,
    ]);
    useEffect(() => {
        const queryParams = new URLSearchParams(history.location.search);
        const variantsAttributesAccessoryTypeEn = queryParams.get(VARIANTS_ACCESSORY_TYPE_EN);
        if (variantsAttributesAccessoryTypeEn) {
            queryParams.delete(VARIANTS_ACCESSORY_TYPE_EN);
            queryParams.set(VARIANTS_ACCESSORY_TYPE, variantsAttributesAccessoryTypeEn);
            history.replace({
                search: queryParams.toString(),
            });
        }
    }, [history, history.location.search]);
    useEffect(() => {
        const hasFilters = Object.values(facetsParams).some(Boolean);
        if (isFilterCleared && hasFilters) {
            setIsFilterCleared(false);
        }
        if (!isFilterCleared && !hasFilters) {
            setIsFilterCleared(true);
        }
    }, [facetsParams, isFilterCleared]);
    const handleSortOrderChange = (value) => {
        if (value !== sortOrder) {
            const queryParams = new URLSearchParams(history.location.search);
            queryParams.set(QueryParams.SORT, value);
            history.push({
                search: queryParams.toString(),
            });
        }
    };
    const onSetDiscountHandler = useCallback(() => {
        const queryParams = new URLSearchParams(history.location.search);
        if (!isShowOnlyDiscounted) {
            setIsShowOnlyDiscounted(true);
            queryParams.set(QueryParams.DISCOUNTED, 'true');
        }
        else {
            setIsShowOnlyDiscounted(false);
            queryParams.delete(QueryParams.DISCOUNTED);
        }
        history.push({
            search: queryParams.toString(),
        });
    }, [history, isShowOnlyDiscounted]);
    const setDefaultStartPage = useCallback(() => {
        setActiveItemValue(DEFAULT_START_PAGE);
        setCurrentPage(DEFAULT_START_PAGE);
    }, [setActiveItemValue]);
    const handleFilterCleared = () => {
        const queryParams = new URLSearchParams(history.location.search);
        facetsConfig.forEach((facetId) => {
            queryParams.delete(facetId);
        });
        history.push({
            search: queryParams.toString(),
        });
        setIsFilterCleared(true);
        if (isShowOnlyDiscounted && !isDiscountFromAEM) {
            onSetDiscountHandler();
        }
        setFacets((prev) => prev.map((facet) => ({
            ...facet,
            values: facet.values.map((value) => ({
                ...value,
                checked: false,
            })),
        })));
    };
    const handleSearch = (word) => {
        const queryParams = new URLSearchParams(history.location.search);
        if (word.length) {
            queryParams.set(QueryParams.SEARCH, word);
        }
        else {
            queryParams.delete(QueryParams.SEARCH);
        }
        history.push({
            search: queryParams.toString(),
        });
    };
    const handleUpdateFacets = useCallback((id, options) => {
        const currentFacet = facets.find((facet) => facet.id === id);
        if (currentFacet) {
            currentFacet.values = currentFacet.values.map((value) => ({
                ...value,
                checked: options?.find((option) => option.value === value.value)?.checked ?? false,
            }));
            setDefaultStartPage();
            const queryParams = new URLSearchParams(history.location.search);
            if (currentFacet.values.some((value) => value.checked)) {
                queryParams.set(currentFacet.id, currentFacet.values
                    .filter((value) => value.checked)
                    .map((value) => value.value)
                    .join(','));
            }
            else {
                queryParams.delete(currentFacet.id);
            }
            history.push({
                search: queryParams.toString(),
            });
        }
    }, [facets, history, setDefaultStartPage]);
    // Prepared Pills data
    const singlePill = useMemo(() => ({
        groupId: VARIANTS_PRICES_DISCOUNT,
        label: t('productsGrid_discounted_toggle'),
        value: isShowOnlyDiscounted,
        handleDelete: () => {
            onSetDiscountHandler();
        },
    }), [isShowOnlyDiscounted, onSetDiscountHandler, t]);
    const multiplePills = selectedFacets
        ? getPillsOptions(selectedFacets, dictionary, handleUpdateFacets, facets)
        : [];
    const isHiddenDiscountPills = isDiscountFromAEM ? false : isShowOnlyDiscounted;
    const isPillsExist = isHiddenDiscountPills || multiplePills?.some((item) => Boolean(item));
    return {
        // common request props
        data: resultsData,
        isDataExists,
        error,
        isLoading,
        // sorting order
        sortingProps: {
            sortOrder,
            sortFilterOptions,
            handleSortOrderChange,
        },
        // clear filter
        filterClearedProps: {
            isFilterCleared,
            handleFilterCleared,
        },
        // Pagination settings
        paginationProps: {
            paginationParam,
            paginationActivePage,
            onClickActivePageHandler,
            paginationConfig,
            currentPerPageValue,
            setCurrentPerPageValue,
        },
        // counter props
        counter,
        // search props
        searchInput: {
            searchInputValue: search,
            handleSearch,
        },
        // discounted api
        discountedApi: {
            isShowOnlyDiscounted,
            onSetDiscountHandler,
            isDiscountFromAEM,
        },
        // facets api
        facetsProps: {
            facets,
            handleUpdateFacets,
        },
        pills: {
            isPillsExist,
            singlePill: isDiscountFromAEM ? undefined : singlePill,
            multiplePills,
        },
    };
}
